import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  ConfirmationNumber as TicketIcon,
  Business as OrgIcon,
  CheckCircle as CompletedIcon,
  RadioButtonUnchecked as OpenIcon,
  HourglassEmpty as PendingIcon,
  PlayCircle as InProgressIcon,
  Assignment as AssignedIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';
import { isPlatformLevel } from '../utils/roleUtils';
import { PLATFORM_BRAND_COLOR, getBrandColor, getBrandContrastText, getBrandHoverTint, getBrandTint } from '../utils/colorUtils';
import StorageTracking from './StorageTracking';
import LastActive from './LastActive';

const Activity = ({ currentUser }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isPlatform = isPlatformLevel(currentUser?.role);
  const accentColor = isPlatform ? PLATFORM_BRAND_COLOR : getBrandColor(theme);
  const accentContrast = isPlatform ? '#ffffff' : getBrandContrastText(theme);
  // Platform users can switch between Last Active (default), the ticket feed, and storage.
  const [viewMode, setViewMode] = useState(isPlatform ? 'lastactive' : 'activities');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const fetchActivity = async () => {
    try {
      setLoading(true);
      console.log('📋 [Activity] Fetching activity...');

      const params = new URLSearchParams();
      if (currentUser?.id) {
        params.append('userId', currentUser.id);
      }
      if (currentUser?.typingCenterId) {
        params.append('typingCenterId', currentUser.typingCenterId);
      }

      const queryString = params.toString();
      const url = queryString ? `/api/dashboard/stats?${queryString}` : '/api/dashboard/stats';

      const response = await ApiService.get(url);

      if (response.data && response.data.success) {
        const data = response.data.data;
        setActivities(data.recentActivity || []);
        console.log('📋 [Activity] Fetched activities:', data.recentActivity?.length);
      }
    } catch (err) {
      console.error('❌ [Activity] Error fetching activity:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchActivity();
    }
  }, [currentUser]);

  // Get status configuration
  const getStatusConfig = (status) => {
    switch (status?.toUpperCase()) {
      case 'OPEN':
        return {
          bgcolor: isDark ? 'rgba(64,128,224,0.15)' : '#e3f2fd',
          color: '#4080e0',
          borderColor: '#4080e0',
          icon: <OpenIcon sx={{ fontSize: 16 }} />,
          label: 'Open'
        };
      case 'ASSIGNED':
        return {
          bgcolor: isDark ? 'rgba(239,108,0,0.15)' : '#fff3e0',
          color: '#ef6c00',
          borderColor: '#ef6c00',
          icon: <AssignedIcon sx={{ fontSize: 16 }} />,
          label: 'Assigned'
        };
      case 'IN_PROGRESS':
      case 'INPROGRESS':
        return {
          bgcolor: isDark ? 'rgba(123,31,162,0.15)' : '#f3e5f5',
          color: isDark ? '#ce93d8' : '#7b1fa2',
          borderColor: isDark ? '#ce93d8' : '#7b1fa2',
          icon: <InProgressIcon sx={{ fontSize: 16 }} />,
          label: 'In Progress'
        };
      case 'COMPLETED':
        return {
          bgcolor: isDark ? 'rgba(46,125,50,0.15)' : '#e8f5e9',
          color: isDark ? '#66bb6a' : '#2e7d32',
          borderColor: isDark ? '#66bb6a' : '#2e7d32',
          icon: <CompletedIcon sx={{ fontSize: 16 }} />,
          label: 'Completed'
        };
      case 'CLOSED':
        return {
          bgcolor: isDark ? 'rgba(46,125,50,0.15)' : '#e8f5e9',
          color: isDark ? '#66bb6a' : '#2e7d32',
          borderColor: isDark ? '#66bb6a' : '#2e7d32',
          icon: <CompletedIcon sx={{ fontSize: 16 }} />,
          label: 'Closed'
        };
      case 'PENDING':
        return {
          bgcolor: isDark ? 'rgba(245,124,0,0.12)' : '#fff8e1',
          color: '#f57c00',
          borderColor: '#f57c00',
          icon: <PendingIcon sx={{ fontSize: 16 }} />,
          label: 'Pending'
        };
      default:
        return {
          bgcolor: 'action.hover',
          color: 'text.secondary',
          borderColor: '#999',
          icon: <TicketIcon sx={{ fontSize: 16 }} />,
          label: status || 'Unknown'
        };
    }
  };

  // Get organization initials and color
  const getOrgAvatar = (orgName) => {
    const name = orgName || 'Unknown';
    const initials = name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

    // Generate consistent color based on name
    const colors = ['#4080e0', '#2e7d32', '#7b1fa2', '#c62828', '#00838f', '#ef6c00', '#5d4037', '#455a64'];
    const colorIndex = name.charCodeAt(0) % colors.length;

    return { initials, color: colors[colorIndex] };
  };

  // Get relative time with better formatting
  const getRelativeTime = (timestamp) => {
    const now = Date.now();
    const activityTime = timestamp * 1000;
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${Math.floor(diffDays / 7)}w ago`;
  };

  // Group activities by date
  const groupActivitiesByDate = (activities) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = {
      today: [],
      yesterday: [],
      earlier: []
    };

    activities.forEach(activity => {
      const activityDate = new Date(activity.createdAt * 1000);
      activityDate.setHours(0, 0, 0, 0);

      if (activityDate.getTime() === today.getTime()) {
        groups.today.push(activity);
      } else if (activityDate.getTime() === yesterday.getTime()) {
        groups.yesterday.push(activity);
      } else {
        groups.earlier.push(activity);
      }
    });

    return groups;
  };

  // Filter activities
  const filteredActivities = activities.filter(activity => {
    const matchesSearch = searchTerm === '' ||
      activity.customerOrganisation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.serviceName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.ticketNumber?.toString().includes(searchTerm) ||
      activity.orgTicketNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'ALL' || activity.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const groupedActivities = groupActivitiesByDate(filteredActivities);

  // Activity Item Component - Compact Design
  const ActivityItem = ({ activity, isLast }) => {
    const statusConfig = getStatusConfig(activity.status);
    const orgAvatar = getOrgAvatar(activity.customerOrganisation);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.25,
          px: 2,
          borderBottom: isLast ? 'none' : '1px solid',
          borderColor: 'divider',
          transition: 'all 0.15s ease',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
      >
        {/* Status Dot */}
        <Box sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: statusConfig.borderColor,
          flexShrink: 0
        }} />

        {/* Organization Avatar */}
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: orgAvatar.color,
            fontSize: '0.7rem',
            fontWeight: 700,
            flexShrink: 0
          }}
        >
          {orgAvatar.initials}
        </Avatar>

        {/* Main Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {activity.customerOrganisation || 'Unknown'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', flexShrink: 0 }}>
              #{activity.ticketNumber}
            </Typography>
            {activity.orgTicketNumber && (
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', flexShrink: 0 }}>
                ({activity.orgTicketNumber})
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {activity.serviceName || 'Service'}
          </Typography>
        </Box>

        {/* Status & Time */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
          <Chip
            label={statusConfig.label}
            size="small"
            sx={{
              bgcolor: statusConfig.bgcolor,
              color: statusConfig.color,
              fontWeight: 600,
              fontSize: '0.65rem',
              height: 20,
              '& .MuiChip-label': { px: 1 }
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
            {getRelativeTime(activity.createdAt)}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Section Header Component - Compact
  const SectionHeader = ({ title, count }) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      px: 2,
      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f8f9fa',
      borderBottom: '1px solid',
      borderColor: 'divider'
    }}>
      <Typography variant="caption" sx={{
        color: 'text.secondary',
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        {title}
      </Typography>
      <Box sx={{
        bgcolor: isPlatform ? 'action.hover' : getBrandTint(theme, 0.12),
        color: isPlatform ? 'text.secondary' : 'secondary.main',
        px: 0.75,
        py: 0.125,
        borderRadius: '4px',
        fontSize: '0.6rem',
        fontWeight: 700
      }}>
        {count}
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.main' }}>
            Activity
          </Typography>
          {isPlatform && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(e, val) => { if (val) setViewMode(val); }}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.8rem' } }}
            >
              <ToggleButton value="lastactive">Last Active</ToggleButton>
              <ToggleButton value="activities">Activities</ToggleButton>
              <ToggleButton value="storage">Storage</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
        {viewMode === 'activities' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${filteredActivities.length} Activities`}
            size="small"
            sx={{
              bgcolor: accentColor,
              color: accentContrast,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 26
            }}
          />
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchActivity}
              disabled={loading}
              size="small"
              sx={{
                bgcolor: isPlatform ? 'action.hover' : getBrandTint(theme, 0.08),
                '&:hover': { bgcolor: isPlatform ? 'action.selected' : getBrandHoverTint(theme, 0.14) }
              }}
            >
              <RefreshIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        )}
      </Box>

      {isPlatform && viewMode === 'lastactive' ? (
        <LastActive />
      ) : isPlatform && viewMode === 'storage' ? (
        <StorageTracking currentUser={currentUser} />
      ) : (
      <>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 180,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              borderRadius: '8px',
              height: 36,
              '& fieldset': { borderColor: 'divider' },
              '&:hover fieldset': { borderColor: 'primary.main' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
            },
            '& .MuiInputBase-input': { fontSize: '0.85rem' }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '8px',
              height: 36,
              fontSize: '0.85rem',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' }
            }}
            startAdornment={
              <InputAdornment position="start">
                <FilterIcon sx={{ color: 'text.disabled', fontSize: 16, ml: 0.5 }} />
              </InputAdornment>
            }
          >
            <MenuItem value="ALL" sx={{ fontSize: '0.85rem' }}>All Status</MenuItem>
            <MenuItem value="OPEN" sx={{ fontSize: '0.85rem' }}>Open</MenuItem>
            <MenuItem value="ASSIGNED" sx={{ fontSize: '0.85rem' }}>Assigned</MenuItem>
            <MenuItem value="IN_PROGRESS" sx={{ fontSize: '0.85rem' }}>In Progress</MenuItem>
            <MenuItem value="COMPLETED" sx={{ fontSize: '0.85rem' }}>Completed</MenuItem>
            <MenuItem value="CLOSED" sx={{ fontSize: '0.85rem' }}>Closed</MenuItem>
            <MenuItem value="PENDING" sx={{ fontSize: '0.85rem' }}>Pending</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Activity Feed */}
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: 'secondary.main' }} />
        </Box>
      ) : filteredActivities.length === 0 ? (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <TicketIcon sx={{ fontSize: 40, color: '#ccc', mb: 1.5 }} />
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
                No activity found
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'Try adjusting your filters'
                  : 'Ticket activity will appear here'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {/* Today */}
          {groupedActivities.today.length > 0 && (
            <>
              <SectionHeader title="Today" count={groupedActivities.today.length} />
              {groupedActivities.today.map((activity, index) => (
                <ActivityItem
                  key={activity.id || `today-${index}`}
                  activity={activity}
                  isLast={index === groupedActivities.today.length - 1 &&
                          groupedActivities.yesterday.length === 0 &&
                          groupedActivities.earlier.length === 0}
                />
              ))}
            </>
          )}

          {/* Yesterday */}
          {groupedActivities.yesterday.length > 0 && (
            <>
              <SectionHeader title="Yesterday" count={groupedActivities.yesterday.length} />
              {groupedActivities.yesterday.map((activity, index) => (
                <ActivityItem
                  key={activity.id || `yesterday-${index}`}
                  activity={activity}
                  isLast={index === groupedActivities.yesterday.length - 1 &&
                          groupedActivities.earlier.length === 0}
                />
              ))}
            </>
          )}

          {/* Earlier */}
          {groupedActivities.earlier.length > 0 && (
            <>
              <SectionHeader title="Earlier" count={groupedActivities.earlier.length} />
              {groupedActivities.earlier.map((activity, index) => (
                <ActivityItem
                  key={activity.id || `earlier-${index}`}
                  activity={activity}
                  isLast={index === groupedActivities.earlier.length - 1}
                />
              ))}
            </>
          )}
        </Card>
      )}
      </>
      )}
    </Box>
  );
};

export default Activity;
